import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { asFetchableView, useViewContext } from '../../../../_lib/ViewFetcher';
import { useModalContext } from '../../../../components/organisms/ModalDialog/ModalContext';
import {
  CategoriesParam,
  ComboBoxMultiselectParam,
  Form,
  Row,
  SonForm,
  SubmitButton,
  useForm,
} from '../../../../_lib/Form2';
import { AddUserSelectComboBox } from './AddUserSelectComboBox';
import { ListBoxModifier } from '../../../../components/molecules/Form/components/ComboBox';
import { useRefreshBlock } from '../../../../_lib/BlockFetcher';
import { useDelayed } from '../../../../hooks/useDelayed';

export type AddUserRoleParam<TName extends string> = {
  active?: boolean;
} & CategoriesParam<TName>;

type AddUserType = {
  form: SonForm<ComboBoxMultiselectParam<'userId'> | AddUserRoleParam<'roles'>>;
};

function AddUser({ form }: AddUserType) {
  const { additionalProps } = useViewContext();
  const { translationNamespace } = additionalProps;

  const { t } = useTranslation();
  const { refresh } = useRefreshBlock();

  const { closeModal } = useModalContext();
  const refreshAndClose = useCallback(() => {
    refresh();
    closeModal();
  }, [closeModal, refresh]);

  const delayedRefreshAndClose = useDelayed(refreshAndClose);
  const onSuccess = () => {
    delayedRefreshAndClose();
  };

  const { formProps, params, fetchState } = useForm({
    form,
    onSuccess,
  });

  const userIdParam = params.getByName('userId');
  const rolesParam = params.getByName('roles');

  return (
    <Form id="add-user-form" {...formProps}>
      {userIdParam && rolesParam && (
        <Row modifier="block">
          <AddUserSelectComboBox
            suggestUrl={userIdParam.comboBoxUrl.url}
            modifierListBox={ListBoxModifier.RELATIVE}
            labelTitle={t(`${translationNamespace}.chooseUser`)}
            required={userIdParam.required}
            rolesParam={rolesParam}
          />
        </Row>
      )}
      <SubmitButton
        buttonLabel={t(`${translationNamespace}.save`)}
        messageSuccess={t(`${translationNamespace}.status.success`)}
        messageSending={t(`${translationNamespace}.status.sending`)}
        messageFail={t(`${translationNamespace}.status.fail`)}
        fetchState={fetchState}
      />
    </Form>
  );
}

export const AddUserView = asFetchableView(AddUser);
